import {
  ALL_RESIDENT_STATES,
  DateSchema,
  RESIDENT_STATE,
  TIME_FRAME,
  calculateResidentStateDurations,
  calculateSleepingHours,
  sort,
} from "@packages/core";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { mean } from "es-toolkit";
import { useEffect, useMemo } from "react";
import { LuArrowLeft, LuArrowRight, LuChevronLeft } from "react-icons/lu";
import {
  Bar,
  BarChart,
  type BarShapeProps,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { Temporal } from "temporal-polyfill";
import * as v from "valibot";

import { Breadcrumbs, ButtonStyle, PageName, TypographyStyle } from "#components";
import {
  fromResidentStateToColor,
  renderStatusChart,
  useEasy,
  useResidentQuery,
  useStatusInfiniteQuery,
  useTranslate,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/residents/$userKey/states",
)({
  component: Component,
  validateSearch(parameters) {
    return v.parse(
      v.partial(
        v.object({
          date: v.fallback(DateSchema(), function () {
            return Temporal.Now.plainDateISO("Asia/Tokyo");
          }),
          numberOfDays: v.fallback(v.pipe(v.number(), v.integer(), v.minValue(1)), 1),
        }),
      ),
      parameters,
    );
  },
});

function Component() {
  const { facilityKey, userKey } = Route.useParams();
  const { date: _date, numberOfDays = 1 } = Route.useSearch();

  const residentQuery = useResidentQuery(userKey);
  const { data: resident } = residentQuery;
  const router = useRouter();
  const translate = useTranslate();
  const [easy] = useEasy();

  const date = useMemo(
    function () {
      return _date ?? Temporal.Now.plainDateISO("Asia/Tokyo");
    },
    [_date],
  );

  const days = useMemo(
    function () {
      return Array.from({ length: numberOfDays }, function (_, index) {
        return date.subtract({ days: numberOfDays - index - 1 });
      });
    },
    [date, numberOfDays],
  );

  const period = useMemo(
    function () {
      return [
        days[0].toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
        days[days.length - 1].add({ days: 1 }).toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
      ] as const;
    },
    [days],
  );

  const statusInfiniteQuery1 = useStatusInfiniteQuery({
    index: "userKey-transitionedAt",
    condition: [
      ["userKey", "=", userKey],
      ["transitionedAt", "<", period[0]],
    ],
    descendingOrder: true,
    limit: 1,
  });

  const statusInfiniteQuery2 = useStatusInfiniteQuery({
    index: "userKey-transitionedAt",
    condition: [
      ["userKey", "=", userKey],
      ["transitionedAt", "BETWEEN", period[0], "AND", period[1]],
    ],
  });

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (statusInfiniteQuery2.hasNextPage && !statusInfiniteQuery2.isFetching) {
        statusInfiniteQuery2.fetchNextPage();
      }
    },
    [statusInfiniteQuery2],
  );

  const statuses = useMemo(
    function () {
      const status = statusInfiniteQuery1.data?.pages.at(0)?.statuses.at(0);
      const statuses =
        statusInfiniteQuery2.data?.pages.flatMap(function (page) {
          return page.statuses;
        }) ?? [];
      if (status) {
        statuses.unshift(status);
      }

      return sort(statuses, function ({ transitionedAt: a }, { transitionedAt: b }) {
        return Temporal.PlainDateTime.compare(a, b);
      });
    },
    [statusInfiniteQuery1.data, statusInfiniteQuery2.data],
  );

  const stateTransitions = useMemo(
    function () {
      return sort(
        statuses.map(function (status) {
          return [status.transitionedAt, status.state] as const;
        }),
        function ([a], [b]) {
          return Temporal.PlainDateTime.compare(a, b);
        },
      );
    },
    [statuses],
  );

  const sleepingHours = useMemo(
    function () {
      const sleepingHours = mean(
        days
          .map(function (day) {
            const now = Temporal.Now.plainDateTimeISO("Asia/Tokyo");
            const start = day.toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] });
            const end = day.add({ days: 1 }).toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] });
            return calculateSleepingHours(
              calculateResidentStateDurations(stateTransitions, {
                period: [start, Temporal.PlainDateTime.compare(end, now) < 0 ? end : now],
              }),
            ).total("minutes");
          })
          .filter(function (minutes) {
            return minutes > 0;
          }),
      );
      return Number.isNaN(sleepingHours) ? 0 : sleepingHours;
    },
    [days, stateTransitions],
  );

  const data = useMemo(
    function () {
      return days.map(function (day) {
        return { x: 24, y: day.toString() };
      });
    },
    [days],
  );

  return (
    <main className={css({ padding: { smDown: "0 !important" } })}>
      <PageName>
        {translate("%Sleep.0%") + " - " + (resident?.name ?? translate("%Loading.0%"))}
      </PageName>

      <div
        className={css({
          alignItems: "center",
          display: "flex",
          gap: "16px",
          hideFrom: "sm",
          justifyContent: "space-between",
          marginTop: "16px",
          marginX: "16px",
        })}
      >
        <Link
          className={css({ alignItems: "center", display: "flex", gap: "4px", hideFrom: "sm" })}
          onClick={function (event) {
            if (router.history.canGoBack()) {
              event.preventDefault();
              event.stopPropagation();
              router.history.back();
            }
          }}
          params={{ facilityKey, userKey }}
          to="/facilities/$facilityKey/residents/$userKey"
        >
          <LuChevronLeft className={css({ height: "32px", width: "auto" })} />
          <p
            className={TypographyStyle({
              color: "primary",
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: 1,
            })}
          >
            {translate("%HonorificTitle.0%", {
              $: resident ?? { name: translate("%Resident.0%") },
            })}
          </p>
        </Link>
      </div>

      <Breadcrumbs
        className={css(TypographyStyle.raw({ fontSize: "14px", fontWeight: 500, lineHeight: 1 }), {
          display: "flex",
          hideBelow: "sm",
        })}
      >
        <Link
          className={css({ color: "#71717A" })}
          params={{ facilityKey }}
          to="/facilities/$facilityKey"
        >
          {translate("%ResidentList.0%")}
        </Link>
        <Link
          className={css({ color: "#71717A" })}
          params={{ facilityKey, userKey }}
          to="/facilities/$facilityKey/residents/$userKey"
        >
          {translate("%HonorificTitle.0%", {
            $: resident ?? { name: translate("%Resident.0%") },
          })}
        </Link>
        <p>{translate("%Sleep.0%")}</p>
      </Breadcrumbs>

      <div
        className={css({
          backgroundColor: "white",
          borderRadius: { sm: "12px" },
          display: "flex",
          flexDirection: "column",
          marginTop: "16px",
          padding: "16px",
        })}
      >
        <h3
          className={css(TypographyStyle.raw({ lineHeight: 1 }), {
            fontSize: { base: "16px", sm: "20px" },
            fontWeight: { base: 500, sm: 700 },
            textAlign: { base: "center", sm: "left" },
          })}
        >
          {translate("%Sleep.0%")}
        </h3>

        <div
          className={css({
            backgroundColor: "muted-background",
            borderRadius: "6px",
            display: "flex",
            marginTop: "12px",
            padding: "4px",
            width: "100%",
          })}
        >
          {[1, 7, 30].map(function (nds, index) {
            return (
              <Link
                className={css(
                  TypographyStyle.raw({
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: 1,
                    textAlign: "center",
                  }),
                  {
                    borderRadius: "2px",
                    display: "block",
                    flexGrow: 1,
                    paddingX: "12px",
                    paddingY: "6px",
                    _selected: { backgroundColor: "background" },
                  },
                )}
                data-selected={numberOfDays === nds || undefined}
                key={index}
                params={{ facilityKey, userKey }}
                replace
                search={{ date, numberOfDays: nds }}
                to="/facilities/$facilityKey/residents/$userKey/states"
              >
                {nds === 1
                  ? translate("%Day.0%")
                  : nds === 7
                    ? translate("%Week.0%")
                    : nds === 30
                      ? translate("%Month.0%")
                      : translate("%NumberOfDays.0%", { $: { numberOfDays: nds } })}
              </Link>
            );
          })}
        </div>

        <p
          className={css(TypographyStyle.raw({ color: "primary", fontSize: "14px" }), {
            marginTop: "12px",
            opacity: 0.6,
          })}
        >
          {numberOfDays === 1
            ? translate("%Date.1%", { $: days[0] })
            : translate("%Date.1%", { $: days[0] }) +
              "〜" +
              translate("%Date.1%", { $: days[days.length - 1] })}
        </p>

        <div className={css({ marginTop: "12px" })}>
          <p
            className={css(
              TypographyStyle.raw({
                color: "primary/60",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: 1,
              }),
            )}
          >
            {translate("%SleepingHours.0%") +
              (days.length > 1 ? " (" + translate("%Average.0%") + ")" : "")}
          </p>
          {statuses.length > 0 ? (
            <div
              className={css({
                alignItems: "baseline",
                display: "flex",
                gap: "2px",
                marginTop: "6px",
              })}
            >
              <span
                className={TypographyStyle({
                  color: "primary",
                  fontSize: "36px",
                  fontWeight: 700,
                  lineHeight: 1,
                })}
              >
                {Math.floor(sleepingHours / 60)}
              </span>
              <span
                className={TypographyStyle({
                  color: "primary/60",
                  fontSize: "18px",
                  fontWeight: 700,
                  lineHeight: 1,
                })}
              >
                {translate("%Hour.0%")}
              </span>
              <span
                className={TypographyStyle({
                  color: "primary",
                  fontSize: "36px",
                  fontWeight: 700,
                  lineHeight: 1,
                })}
              >
                {Math.floor(sleepingHours % 60)}
              </span>
              <span
                className={TypographyStyle({
                  color: "primary/60",
                  fontSize: "18px",
                  fontWeight: 700,
                  lineHeight: 1,
                })}
              >
                {translate("%Minute.0%")}
              </span>
            </div>
          ) : (
            <p className={TypographyStyle({ color: "primary", fontSize: "36px", fontWeight: 700 })}>
              {translate("%NoData.1%")}
            </p>
          )}
        </div>

        <div
          className={css({
            alignItems: "center",
            display: "flex",
            gap: "16px",
            justifyContent: "center",
          })}
        >
          <Link
            className={css(
              ButtonStyle.raw({
                appearance: "outlined",
                borderRadius: "100%",
                color: "outlined",
                icon: true,
                size: "32px",
              }),
              { hideBelow: "sm" },
            )}
            search={{ date: date.subtract({ days: numberOfDays }), numberOfDays }}
            to="."
          >
            <LuArrowLeft className={css({ height: "16px", width: "auto" })} />
          </Link>

          <ResponsiveContainer height={420} width="100%">
            <BarChart
              barCategoryGap={days.length <= 7 ? 12 : 4}
              barGap={days.length <= 7 ? 12 : 4}
              barSize={days.length === 1 ? 120 : days.length <= 7 ? 24 : 6}
              data={data}
              layout="vertical"
            >
              <XAxis
                allowDataOverflow
                axisLine={false}
                dataKey="x"
                tick={{ fill: "#18181B4D", fontSize: "12px" }}
                tickFormatter={function (hour) {
                  return hour < 24 ? hour + 6 + ":00" : "";
                }}
                tickLine={false}
                ticks={[0, 6, 12, 18, 24]}
                type="number"
              />
              <YAxis
                axisLine={false}
                dataKey="y"
                padding={{
                  top: days.length === 1 ? 105 : days.length <= 7 ? 45 : 15,
                  bottom: days.length === 1 ? 105 : days.length <= 7 ? 45 : 15,
                }}
                tick={{ fill: "#18181B4D", fontSize: "12px" }}
                tickFormatter={function (epoch) {
                  return numberOfDays > 7
                    ? translate("%Day.1%", { $: Temporal.PlainDate.from(epoch) })
                    : translate("%DayOfWeek." + Temporal.PlainDate.from(epoch).dayOfWeek + ".0%");
                }}
                tickLine={false}
                ticks={useMemo(
                  function () {
                    const start = date.subtract({ days: numberOfDays - 1 });
                    return Array.from({ length: start.until(date).days + 1 }, function (_, i) {
                      return start.add({ days: i }).toString();
                    }).filter(function (_, index, { length }) {
                      return numberOfDays > 7
                        ? index === 0 ||
                            index === length - 1 ||
                            index % Math.round(length / 4) === 0
                        : true;
                    });
                  },
                  [date, numberOfDays],
                )}
                type="category"
                width={numberOfDays > 7 ? 32 : numberOfDays > 1 ? 16 : 0}
              />
              <Legend
                content={function () {
                  return (
                    <div
                      className={css({
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "24px",
                        justifyContent: "center",
                        marginTop: "12px",
                      })}
                    >
                      {(easy
                        ? [
                            {
                              backgroundColor: fromResidentStateToColor(RESIDENT_STATE.SLEEPING),
                              name: translate("%ResidentState." + RESIDENT_STATE.SLEEPING + ".0%"),
                              residentState: RESIDENT_STATE.SLEEPING,
                            },
                            {
                              backgroundColor: fromResidentStateToColor(RESIDENT_STATE.WAKING),
                              name: translate("%ResidentState." + RESIDENT_STATE.WAKING + ".0%"),
                              residentState: RESIDENT_STATE.WAKING,
                            },
                            {
                              backgroundColor: fromResidentStateToColor(RESIDENT_STATE.GOT_UP),
                              name: translate("%ResidentState." + RESIDENT_STATE.GOT_UP + ".0%"),
                              residentState: RESIDENT_STATE.GOT_UP,
                            },
                            {
                              backgroundColor: fromResidentStateToColor(RESIDENT_STATE.UNKNOWN),
                              name: translate("%Others.0%"),
                              residentState: RESIDENT_STATE.UNKNOWN,
                            },
                          ]
                        : ALL_RESIDENT_STATES.map(function (residentState) {
                            return {
                              backgroundColor: fromResidentStateToColor(residentState),
                              name: translate("%ResidentState." + residentState + ".0%"),
                              residentState,
                            };
                          })
                      ).map(function ({ backgroundColor, name, residentState }) {
                        return (
                          <div
                            className={css({ alignItems: "center", display: "flex", gap: "4px" })}
                            key={residentState}
                          >
                            <div
                              className={css({
                                aspectRatio: "1 / 1",
                                borderRadius: "2px",
                                height: "14px",
                              })}
                              style={{ backgroundColor }}
                            />
                            <p
                              className={TypographyStyle({
                                color: "primary/60",
                                fontSize: "14px",
                                lineHeight: 1,
                              })}
                            >
                              {name}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
              <CartesianGrid horizontal={false} stroke="#E4E4E7" syncWithTicks />
              <Bar
                dataKey="x"
                fill="#0000"
                shape={function ({ height, index, width, x, y }: BarShapeProps) {
                  if (typeof index !== "number" || typeof width !== "number") {
                    return <path />;
                  }

                  const day = days.at(index);
                  if (!day) {
                    return <path />;
                  }

                  return (
                    <image
                      height={height}
                      href={renderStatusChart(
                        {
                          from: day.toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
                          numberOfPixels: width,
                          statuses,
                          to: day.add({ days: 1 }).toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
                        },
                        { easy },
                      ).toDataURL()}
                      preserveAspectRatio="none"
                      width={width}
                      x={x}
                      y={y}
                    />
                  );
                }}
              />
            </BarChart>
          </ResponsiveContainer>

          <Link
            className={css(
              ButtonStyle.raw({
                appearance: "outlined",
                borderRadius: "100%",
                color: "outlined",
                icon: true,
                size: "32px",
              }),
              { hideBelow: "sm" },
            )}
            search={{ date: date.add({ days: numberOfDays }), numberOfDays }}
            to="."
          >
            <LuArrowRight className={css({ height: "16px", width: "auto" })} />
          </Link>
        </div>
      </div>
    </main>
  );
}
